import React, { useEffect } from "react";
import { useState } from "react";
import MFab from "@/components/ui/atoms/MFab";
import { useTranslation } from "react-i18next";
import RemindersListing from "./RemindersListing";
import AddReminderMemberDrawer from "./AddReminderMemberDrawer";
import { getMemberInfo } from "@/hooks/useMembers";
import MDropDownDrawer from "@/components/ui/molecules/MDropDownDrawer";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DerivedMemberType, Member } from "@/types/member";
import { ReminderType } from "@/types/reminders";
import {
  IconCalendar,
  IconPillFilled,
  IconShieldHeart,
  IconVaccine,
  IconPaw,
  IconActivity,
} from "@tabler/icons-react";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { setSelectedMember, clearSelectedMember } from "@/store/authSlice";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import { useManagedMembersContext } from "@/context/ManagedMembersContext";
import { trackUserDataLoaded } from "@/utils/gtm";
import {
  ANALYTICS_SCREEN_TYPES,
  ANALYTICS_SCREEN_NAMES,
} from "@/constants/analytics.constants";
import styles from "./reminder.module.css";

/**
 * Reminders page component
 * Shows a list of reminders if they exist, otherwise shows a create reminder button
 */
const Reminders: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const loggedInMember = useSelector((state: RootState) => state.auth?.member);
  const memberInfo = getMemberInfo();
  const [memberDrawerOpen, setMemberDrawerOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { managedMembers, fetchManagedMembers } = useManagedMembersContext();

  // Needed up-front (not just when the drawer opens) so openAddReminderFlow
  // can decide whether there's anyone else to choose before the user taps
  // the FAB — see AddReminderMemberDrawer.tsx, whose own managedMembers
  // list always includes "you" alongside anyone else you manage.
  useEffect(() => {
    fetchManagedMembers();
  }, []);

  // Reset to logged-in user on reminders page mount
  // COMMENTED OUT: Preserve selected member when navigating to reminders landing
  // React.useEffect(() => {
  //   if (loggedInMember) {
  //     dispatch(clearSelectedMember());
  //   }
  // }, []);

  // Track page load once
  useEffect(() => {
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
      screenName: ANALYTICS_SCREEN_NAMES.REMINDER_HOME,
    });
  }, []);

  // Icon shown in front of each row — matches the Figma "Select reminder
  // type" drawer spec (adult/child/pet variants use the same icon set).
  const REMINDER_TYPE_ICON: Partial<Record<ReminderType, React.ReactNode>> = {
    [ReminderType.APPOINTMENT]: <IconCalendar size={24} />,
    [ReminderType.MEDICATION]: <IconPillFilled size={24} />,
    [ReminderType.VACCINATION]: <IconVaccine size={24} />,
    [ReminderType.VITALS]: <IconActivity size={24} />,
    [ReminderType.INSURANCE]: <IconShieldHeart size={24} />,
    [ReminderType.PET_CARE]: <IconPaw size={24} />,
  };

  const getReminderItems = (): {
    value: string;
    label: string;
    icon: React.ReactNode;
  }[] => {
    // Order and membership differ per derived member type — matches the
    // adult/child/pet Figma variants exactly (e.g. child shows Vaccination
    // before Vitals tracking, pet shows Vaccination after Insurance).
    let items: ReminderType[];
    if (memberInfo.derivedMemberType === DerivedMemberType.P) {
      items = [
        ReminderType.APPOINTMENT,
        ReminderType.MEDICATION,
        ReminderType.INSURANCE,
        ReminderType.VACCINATION,
        ReminderType.PET_CARE,
      ];
    } else if (memberInfo.derivedMemberType === DerivedMemberType.C) {
      items = [
        ReminderType.APPOINTMENT,
        ReminderType.MEDICATION,
        ReminderType.VACCINATION,
        ReminderType.VITALS,
        ReminderType.INSURANCE,
      ];
    } else {
      items = [
        ReminderType.APPOINTMENT,
        ReminderType.MEDICATION,
        ReminderType.VITALS,
        ReminderType.INSURANCE,
      ];
    }
    return items.map((item) => ({
      value: item,
      label: t(`reminders.addTypeLabels.${item}`),
      icon: (
        <span className={styles.reminderTypeIcon}>
          {REMINDER_TYPE_ICON[item]}
        </span>
      ),
    }));
  };

  // "Who is this reminder about?" step — switches the active member context
  // (same mechanism the family profile picker uses elsewhere) so the
  // reminder-type list and the create pages that follow all resolve to the
  // chosen member via getMemberInfo() on their next render.
  const handleSelectReminderMember = (member: Member) => {
    if (member.id === loggedInMember?.id) {
      dispatch(clearSelectedMember());
    } else {
      dispatch(setSelectedMember(member));
    }
    setMemberDrawerOpen(false);
    setDrawerOpen(true);
  };

  // Skip the "Who is this reminder about?" drawer entirely when there's no
  // one else to choose from — managedMembers always includes yourself
  // (AddReminderMemberDrawer's own isYou check relies on that), so length
  // <= 1 means you're the only person you can create a reminder for.
  // Awaits the mount-time fetch (rather than trusting whatever's already in
  // context) so a fast tap right after landing on this page — before that
  // fetch resolves — can't read an empty managedMembers list and wrongly
  // skip the drawer for someone who actually manages other family members.
  const openAddReminderFlow = async () => {
    const members =
      managedMembers.length > 0 ? managedMembers : await fetchManagedMembers();
    if (members.length <= 1 && loggedInMember) {
      handleSelectReminderMember(loggedInMember);
      return;
    }
    setMemberDrawerOpen(true);
  };

  const handleCreateReminderType = (type: string) => {
    setDrawerOpen(false);
    switch (type) {
      case ReminderType.APPOINTMENT:
        navigate("/reminders/appointment");
        break;
      case ReminderType.INSURANCE:
        navigate("/reminders/insurance");
        break;
      case ReminderType.MEDICATION:
        navigate("/reminders/medication");
        break;
      case ReminderType.VITALS:
        navigate("/reminders/vitals");
        break;
      case ReminderType.VACCINATION:
        navigate("/reminders/vaccination-tracker");
        break;
      case ReminderType.PET_CARE:
        navigate("/reminders/pet-care");
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (searchParams.get("mode") === "add") {
      openAddReminderFlow();
      setSearchParams({});

      // Track add reminder when drawer opens
      trackUserDataLoaded({
        memberInfo,
        screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
        screenName: ANALYTICS_SCREEN_NAMES.REMINDER_ADD,
      });
    }
  }, [searchParams]);

  return (
    <>
      <div className={`profile-page-root ${styles.pageRoot}`}>
        <RemindersListing
          onAddReminder={() => {
            // Track add reminder when button is clicked
            trackUserDataLoaded({
              memberInfo,
              screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
              screenName: ANALYTICS_SCREEN_NAMES.REMINDER_ADD,
            });
            openAddReminderFlow();
          }}
        />

        <AddReminderMemberDrawer
          open={memberDrawerOpen}
          onClose={() => setMemberDrawerOpen(false)}
          onSelectMember={handleSelectReminderMember}
        />

        <MDropDownDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          title={t("reminders.selectReminderType")}
          items={getReminderItems()}
          onSelect={handleCreateReminderType}
        />
      </div>
      <MFab />
    </>
  );
};

export default Reminders;
